import React from 'react'
import toast from 'react-hot-toast'
import { ReportServiceV4, ReportTemplateService } from 'services'
import { CreateReportRequest, ReportStatus, ScheduleInterval } from 'models'
import { HotToast } from 'ui/components/toast'

const POLL_INTERVAL = 5000

const FALLBACK_ERROR_DESCRIPTION = 'Please contact our support team.'

export const getApiErrorDescription = (error: unknown, fallback: string = FALLBACK_ERROR_DESCRIPTION): string => {
  const data = (error as { response?: { data?: { message?: unknown; detail?: unknown } } })?.response?.data
  const message = typeof data?.message === 'string' ? data.message : undefined
  const detail = typeof data?.detail === 'string' ? data.detail : undefined
  return message || detail || fallback
}

const pollAndDownloadReport = async (reportId: string, reportName: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      try {
        const report = await ReportServiceV4.getReport(reportId)

        if (report.status === ReportStatus.COMPLETED) {
          clearInterval(interval)
          const blob = await ReportServiceV4.downloadReport(reportId)
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `${reportName}.pdf`
          link.click()
          window.URL.revokeObjectURL(url)
          resolve()
        }

        if (report.status === ReportStatus.FAILED) {
          clearInterval(interval)
          reject(new Error(report.error_message || 'Report generation failed'))
        }
      } catch (error) {
        clearInterval(interval)
        reject(error)
      }
    }, POLL_INTERVAL)
  })
}

export const generateAndDownloadReport = async (request: CreateReportRequest): Promise<void> => {
  const report = await ReportServiceV4.createReport(request)
  await pollAndDownloadReport(report.id, request.name)

  toast.success(() => React.createElement(HotToast, { text: 'Program Report Successfully Downloaded' }))
}

export const generateAndDownloadFromTemplate = async (
  templateId: string,
  templateName: string
): Promise<void> => {
  const report = await ReportTemplateService.createReportFromTemplate(templateId)
  await pollAndDownloadReport(report.id, templateName)

  toast.success(() => React.createElement(HotToast, { text: 'Report Successfully Downloaded' }))
}

export const formatScheduleFrequency = (interval: ScheduleInterval): string => {
  switch (interval) {
    case ScheduleInterval.ONE_HOUR:
      return 'hourly'
    case ScheduleInterval.ONE_DAY:
      return 'daily'
    case ScheduleInterval.WEEKLY:
      return 'weekly'
    case ScheduleInterval.MONTHLY:
      return 'monthly'
    case ScheduleInterval.QUARTERLY:
      return 'quarterly'
    case ScheduleInterval.SEMI_ANNUAL:
      return 'every 6 months'
    case ScheduleInterval.YEARLY:
      return 'annually'
    default:
      return ''
  }
}

const addInterval = (date: Date, interval: ScheduleInterval, count: number): Date => {
  const result = new Date(date.getTime())
  switch (interval) {
    case ScheduleInterval.ONE_HOUR:
      result.setHours(result.getHours() + count)
      break
    case ScheduleInterval.ONE_DAY:
      result.setDate(result.getDate() + count)
      break
    case ScheduleInterval.WEEKLY:
      result.setDate(result.getDate() + 7 * count)
      break
    case ScheduleInterval.MONTHLY:
      result.setMonth(result.getMonth() + count)
      break
    case ScheduleInterval.QUARTERLY:
      result.setMonth(result.getMonth() + 3 * count)
      break
    case ScheduleInterval.SEMI_ANNUAL:
      result.setMonth(result.getMonth() + 6 * count)
      break
    case ScheduleInterval.YEARLY:
      result.setFullYear(result.getFullYear() + count)
      break
  }
  return result
}

export const computeNextRun = (startDate: string, interval: ScheduleInterval): Date => {
  const start = new Date(startDate)
  const now = new Date()
  if (start > now) return start
  let next = start
  let count = 0
  while (next <= now) {
    count += 1
    next = addInterval(start, interval, count)
  }
  return next
}

export const formatScheduleDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
